/**
 * Admin Approval Show — full bound payload (admin-only per COP-024) +
 * chronological decision audit trail (`tf_approval_decision[]`, D-15).
 *
 * Cites:
 *  - APPR-07 — admin approvals show queryable via REST.
 *  - APPR-09 — full trace is admin-only; this view surfaces the unredacted
 *    payload that the user widget intentionally does NOT show.
 *  - COP-023 — admin review surface.
 *  - COP-024 — admin sees the bound payload in full.
 *  - COP-148 D8 — Execution + Execution error fields: whether the approved
 *    action ever ran, and why it did not. Read-only, no action affordance.
 *  - D-15 — decisions[] audit trail shape (one row per decide call).
 *  - D-16 — admin SPA approvals tab; this Show is the click-through target
 *    from the List view.
 *
 * Threat mitigations (Plan 05-12 register):
 *  - T-05-111 (Info Disclosure / XSS via payload JSON) — payload rendered as a
 *    React text node inside <pre>{JSON.stringify(...)}</pre>. The raw-HTML
 *    React escape hatch is intentionally NOT used here. React's default
 *    escaping handles all keys / values regardless of prototype pollution
 *    attempts.
 *  - T-05-112 (Auth bypass via missing scoping) — fetch requires `userId` from
 *    the URL search params; Plan 05-08's `adminGetApproval` endpoint scopes the
 *    read inside `withUserRole(targetUserId)` (HUB-024 §6) and the adapter
 *    asserts `row.userId === targetUserId` as defense-in-depth.
 */
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import type {
  ApprovalShowResponse,
} from '@verybigthings/trackframe-shared/schemas';
import { Badge } from '@/components/ui/badge';
import { buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert } from '@/components/ui/alert';
import { PageHeader } from '@/components/PageHeader';
import { fmtIso } from '@/lib/format';
import { cn } from '@/lib/utils';
import { hub } from '@/lib/hub-client';
import { usePrincipalLabels } from '@/lib/use-principal-labels';
import { ExecutionBadge } from './execution-badge';

export function ApprovalsShow() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  const [data, setData] = useState<ApprovalShowResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Called before the early returns below — hooks must run unconditionally.
  const labels = usePrincipalLabels();

  useEffect(() => {
    let cancelled = false;
    if (!id || !userId) {
      setError(
        'Missing id or userId in URL — open this page via the Approvals list so the userId query param is forwarded for RLS scoping (HUB-024 §6).',
      );
      setData(null);
      return () => {
        cancelled = true;
      };
    }
    setError(null);
    setData(null);
    hub
      .adminGetApproval({ params: { id }, query: { userId } })
      .then((res: ApprovalShowResponse) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError((err as Error).message ?? 'Failed to load approval');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id, userId]);

  if (error) {
    return (
      <Card>
        <CardContent className="space-y-3 py-4">
          <Alert>{error}</Alert>
          <Link
            to="/admin/approvals"
            className={cn(buttonVariants({ size: 'sm', variant: 'outline' }))}
          >
            Back to approvals
          </Link>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="py-4 text-sm text-muted-foreground">Loading…</CardContent>
      </Card>
    );
  }

  const { approval, decisions } = data;
  const userEmail = labels.userEmail(approval.userId);
  const trackName = approval.trackId ? labels.trackName(approval.trackId) : undefined;

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          <>
            Approval — <code className="font-mono text-base">{approval.toolName}</code>
          </>
        }
        description="Admin-only review surface. Full bound payload below (COP-024)."
        actions={[{ label: 'Back', to: '/admin/approvals', variant: 'outline', size: 'sm' }]}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Metadata</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm md:grid-cols-2">
            <div className="flex justify-between gap-2 border-b pb-1">
              <dt className="text-muted-foreground">Status</dt>
              <dd>
                <Badge>{approval.status}</Badge>
              </dd>
            </div>
            {/* COP-148 D8 — execution truth, read-only. No action affordance. */}
            <div className="flex justify-between gap-2 border-b pb-1">
              <dt className="text-muted-foreground">Execution</dt>
              <dd>
                <ExecutionBadge
                  executionStatus={approval.executionStatus}
                  executionError={approval.executionError}
                />
              </dd>
            </div>
            <div className="flex justify-between gap-2 border-b pb-1">
              <dt className="text-muted-foreground">Created</dt>
              <dd className="font-mono text-xs">{fmtIso(approval.createdAt)}</dd>
            </div>
            <div className="flex justify-between gap-2 border-b pb-1">
              <dt className="text-muted-foreground">Expires</dt>
              <dd className="font-mono text-xs">{fmtIso(approval.expiresAt)}</dd>
            </div>
            <div className="flex justify-between gap-2 border-b pb-1">
              <dt className="text-muted-foreground">Decided</dt>
              <dd className="font-mono text-xs">{fmtIso(approval.decidedAt)}</dd>
            </div>
            <div className="flex justify-between gap-2 border-b pb-1">
              <dt className="text-muted-foreground">User</dt>
              <dd className="text-right text-xs">
                {userEmail ? <div>{userEmail}</div> : null}
                <div className="font-mono text-muted-foreground">{approval.userId}</div>
              </dd>
            </div>
            <div className="flex justify-between gap-2 border-b pb-1">
              <dt className="text-muted-foreground">Track</dt>
              <dd className="text-right text-xs">
                {trackName ? <div>{trackName}</div> : null}
                <div className="font-mono text-muted-foreground">
                  {approval.trackId ?? '—'}
                </div>
              </dd>
            </div>
            <div className="flex justify-between gap-2 border-b pb-1">
              <dt className="text-muted-foreground">Session</dt>
              <dd className="font-mono text-xs">{approval.sessionId}</dd>
            </div>
            <div className="flex justify-between gap-2 border-b pb-1">
              <dt className="text-muted-foreground">Tool call</dt>
              <dd className="font-mono text-xs">{approval.toolCallId}</dd>
            </div>
            <div className="flex justify-between gap-2 border-b pb-1 md:col-span-2">
              <dt className="text-muted-foreground">Payload hash</dt>
              <dd>
                <code className="font-mono text-xs">{approval.payloadHash}</code>
              </dd>
            </div>
            <div className="flex justify-between gap-2 border-b pb-1 md:col-span-2">
              <dt className="text-muted-foreground">Summary</dt>
              <dd className="text-xs">{approval.summary ?? '—'}</dd>
            </div>
            <div className="flex justify-between gap-2 border-b pb-1 md:col-span-2">
              <dt className="text-muted-foreground">Reason</dt>
              <dd className="text-xs">{approval.reason ?? '—'}</dd>
            </div>
            <div className="flex justify-between gap-2 border-b pb-1 md:col-span-2">
              <dt className="text-muted-foreground">Execution error</dt>
              <dd className="text-xs">{approval.executionError ?? '—'}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Bound payload — admin-only (COP-024)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/*
            T-05-111: Render as a React text node inside <pre>. JSON.stringify
            produces a string; React escapes it. The raw-HTML escape hatch is
            intentionally avoided here — using it would allow a prototype-
            polluted key in the bound payload to inject markup.
          */}
          <pre className="max-h-96 overflow-auto rounded-md border bg-muted px-3 py-2 text-xs">
            {JSON.stringify(approval.payload, null, 2)}
          </pre>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Decision audit ({decisions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {decisions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No decisions yet.</p>
          ) : (
            <ol className="space-y-2 text-sm">
              {decisions.map((d) => (
                <li key={d.id} className="rounded-md border bg-background px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <span>
                      <strong className="capitalize">{d.action}</strong> by{' '}
                      <code className="font-mono text-xs">{d.actor}</code>
                    </span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {fmtIso(d.at)}
                    </span>
                  </div>
                  {d.reason ? (
                    <div className="mt-1 text-xs">
                      <span className="text-muted-foreground">Reason: </span>
                      <span>{d.reason}</span>
                    </div>
                  ) : null}
                  <div className="mt-1 text-xs">
                    <span className="text-muted-foreground">Hash: </span>
                    <code className="font-mono text-xs">{d.payloadHash}</code>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
